import { useEffect, useRef } from 'react';

/**
 * Persists the scroll position to sessionStorage as the user scrolls, and
 * restores it after a back-navigation (POP) once all products have loaded.
 *
 * @param {object} params
 * @param {React.MutableRefObject<boolean>} params.isPopRef        - true when the page was reached via back/forward
 * @param {React.MutableRefObject<boolean>} params.scrollRestoredRef - set to true once scroll has been restored
 * @param {React.MutableRefObject<number>}  params.targetPagesRef  - the number of pages that should be loaded before restoring
 * @param {string}  params.status           - Algolia search status
 * @param {number}  params.currentPageCount - how many pages of products are currently rendered
 * @param {number}  params.itemsLength      - current hit count
 * @param {boolean} params.loading          - whether static product data is still loading
 * @param {number}  params.productIdsLength - deferred product ID count (used to detect deferral lag)
 * @param {number}  params.productIdsRawLength - raw product ID count
 */
const useScrollPersistence = ({
  isPopRef,
  scrollRestoredRef,
  targetPagesRef,
  status,
  currentPageCount,
  itemsLength,
  loading,
  productIdsLength,
  productIdsRawLength
}) => {
  const rafIdRef = useRef(null);

  // Save scroll position on every scroll event
  useEffect(() => {
    const scrollKey = `plp-scroll:${window.location.pathname}`;
    // Capture the pathname at mount time so we can detect when Next.js has
    // navigated away. router.push() updates window.location synchronously,
    // so by the time a queued RAF fires the pathname will already be the new
    // page — we must not overwrite the saved position in that case.
    const initialPathname = window.location.pathname;
    const persistScroll = () => {
      if (window.location.pathname !== initialPathname) {
        rafIdRef.current = null;
        return;
      }
      sessionStorage.setItem(scrollKey, String(window.scrollY));
      rafIdRef.current = null;
    };
    const handleScroll = () => {
      // Don't overwrite the saved position while we're waiting to restore it
      if (isPopRef.current && !scrollRestoredRef.current) return;
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(persistScroll);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore scroll position after all pages and products have loaded
  useEffect(() => {
    if (!isPopRef.current || scrollRestoredRef.current) return;
    if (status !== 'idle' || !itemsLength || loading) return;
    if (productIdsLength !== productIdsRawLength) return;
    if (currentPageCount - 1 >= targetPagesRef.current) {
      scrollRestoredRef.current = true;
      const saved = sessionStorage.getItem(`plp-scroll:${window.location.pathname}`);
      if (saved) {
        const y = parseInt(saved, 10);
        if (!Number.isNaN(y) && y > 0) {
          window.scrollTo({
            top: y,
            behavior: 'smooth'
          });
        }
      }
    }
    // isPopRef, scrollRestoredRef, targetPagesRef are refs — intentionally excluded
    // so the effect always reads their latest .current value without re-running on ref identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, currentPageCount, itemsLength, loading, productIdsLength, productIdsRawLength]);
};
export default useScrollPersistence;